import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Board } from "@/components/game/Board";
import { Keyboard } from "@/components/game/Keyboard";
import {
  HostPanel,
  Players,
  Podium,
  ReactionBar,
  ReactionStream,
  RoundBar,
  RoundEnd,
} from "@/components/game/panels";
import { Button, TileMark, Toast, Wordmark } from "@/components/primitives";
import * as api from "@/lib/api";
import { useStore } from "@/lib/store";
import type { RoomState, Settings } from "@/lib/types";
import { cn } from "@/lib/utils";

export default function Room() {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const {
    identity,
    room,
    status,
    error,
    toast,
    shakeSeq,
    revealIndex,
    pending,
    reactions,
    me,
    isHost,
    connect,
    disconnect,
    send,
    guess,
    say,
  } = useStore();

  const [draft, setDraft] = useState("");
  const [localShake, setLocalShake] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // A refresh lands straight here with a token in hand, so reconnect rather
  // than bouncing the player back through the gate.
  useEffect(() => {
    if (!identity) return;
    if (status === "idle") connect(code.toUpperCase());
  }, [identity, status, code, connect]);

  useEffect(() => {
    if (identity === null && status === "idle") {
      const timer = setTimeout(() => {
        if (!api.store.token()) navigate("/join", { replace: true });
      }, 900);
      return () => clearTimeout(timer);
    }
  }, [identity, status, navigate]);

  const phase = room?.phase ?? "lobby";
  const wordLength = room?.wordLength ?? 0;

  // A new round, a submitted guess, or the end of a round all clear the draft.
  useEffect(() => {
    setDraft("");
  }, [room?.round, phase, room?.board.length]);

  const finishedForMe = !!me?.solved || !!me?.out;
  const canType = !isHost && phase === "playing" && !finishedForMe && !pending;

  const submit = useCallback(() => {
    if (!canType || !room) return;
    if (draft.length < room.wordLength) {
      setLocalShake((n) => n + 1);
      say("Not enough letters");
      return;
    }
    guess(draft);
  }, [canType, draft, guess, room, say]);

  const type = useCallback(
    (letter: string) => {
      if (!canType) return;
      setDraft((prev) => (prev.length >= wordLength ? prev : prev + letter));
    },
    [canType, wordLength],
  );

  const back = useCallback(() => {
    if (!canType) return;
    setDraft((prev) => prev.slice(0, -1));
  }, [canType]);

  // Physical keyboard. The on-screen one is the same three handlers.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;

      if (event.key === "Enter") {
        event.preventDefault();
        submit();
        return;
      }
      if (event.key === "Backspace") {
        event.preventDefault();
        back();
        return;
      }
      if (/^[a-zA-Z]$/.test(event.key)) type(event.key.toLowerCase());
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [submit, back, type]);

  const showRoundEnd =
    !!room &&
    (room.phase === "roundEnd" || room.phase === "gameOver") &&
    room.results.length > 0 &&
    dismissed !== room.round;

  const settings = useCallback(
    (patch: Partial<Settings>) => send({ type: "settings", patch }),
    [send],
  );

  const copyCode = useCallback(() => {
    navigator.clipboard?.writeText(code.toUpperCase()).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      },
      () => say("Could not copy"),
    );
  }, [code, say]);

  const status_line = useMemo(() => {
    if (!room) return "Connecting";
    if (room.phase === "lobby") {
      return isHost ? "Set the room up, then start round 1." : "Waiting for the host to start.";
    }
    if (room.phase === "gameOver") return "Game over.";
    if (room.phase === "roundEnd") {
      return isHost ? "Start the next round when the room is ready." : "Round over.";
    }
    if (isHost) return "Round running. Nobody can see your screen.";
    if (me?.solved) return "Solved. Sit tight for the others.";
    if (me?.out) return "Out of guesses. The word lands at round end.";
    // The pills under this line already carry the shape of the round.
    return "";
  }, [room, isHost, me]);

  /* ------------------------------------------------------------- refused */

  if (error) {
    return (
      <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 px-5 text-center">
        <Wordmark className="text-[18px] text-ink-dim" />
        <p className="max-w-[36ch] text-[15px] text-ink">{error}</p>
        <Link to="/join" className="btn btn-ghost">
          Back to the gate
        </Link>
      </main>
    );
  }

  if (!room) {
    return (
      <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-4">
        <Wordmark className="text-[20px]" />
        <p className="text-[13px] uppercase tracking-[0.14em] text-ink-faint">
          {status === "connecting" ? "Connecting" : "Loading room"}
        </p>
      </main>
    );
  }

  /* ---------------------------------------------------------------- room */

  return (
    /**
     * Two layouts, one tree. On a laptop the whole room is pinned to the
     * viewport and nothing scrolls. On a phone the board and keyboard own the
     * first screenful exactly, and the sidebar — players, and the host's
     * controls — sits below it, reached by scrolling. Clipping that panel to a
     * viewport that was already full is what used to put the host's buttons
     * somewhere no thumb could reach.
     */
    <main className="relative flex min-h-[100dvh] flex-col lg:h-[100dvh] lg:min-h-0 lg:overflow-hidden">
      <AnimatePresence>{toast && <Toast key={toast.id} text={toast.text} />}</AnimatePresence>

      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-2 border-b border-line bg-bg px-3 sm:gap-3 sm:px-4 lg:static">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <Link to="/" className="hidden sm:block">
            <TileMark size={18} />
          </Link>
          <button
            type="button"
            onClick={copyCode}
            title="Copy the room code"
            className="wordmark rounded-[4px] border border-line px-2.5 py-1 text-[13px] tracking-[0.22em] transition-colors hover:border-line-bright"
          >
            {copied ? "Copied" : room.code}
          </button>
        </div>

        {/* On a phone the code, the wordmark and Leave cannot all fit — they
            were landing on top of each other. The one you do not need while
            sitting in a room is the wordmark. */}
        <Wordmark className="hidden shrink-0 sm:block sm:text-[21px]" />

        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
          <span className="hidden text-[12px] text-ink-dim sm:inline">
            {isHost ? "Host" : identity?.username}
          </span>
          {/* Reconnects happen on their own; this only says where things stand. */}
          <span
            className={cn(
              "flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em]",
              status === "open" ? "text-ink-faint" : "text-near",
            )}
            title={status}
          >
            <span
              className={cn(
                "size-2 rounded-full",
                status === "open" ? "bg-hit" : status === "connecting" ? "bg-near" : "bg-miss",
              )}
            />
            {status !== "open" && <span className="hidden sm:inline">Reconnecting</span>}
          </span>
          <Button
            variant="ghost"
            className="h-8 px-2.5 text-[11px]"
            onClick={() => {
              disconnect();
              navigate("/");
            }}
          >
            Leave
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* ------------------------------------------------------- board */}
        {/* 3.5rem of header plus 2rem of the panel below left showing: the top
            edge of the sidebar is the only thing that tells a phone there is
            more underneath. */}
        <section className="flex h-[calc(100dvh-5.5rem)] min-h-0 flex-col px-3 py-3 sm:px-5 lg:h-auto lg:flex-1">
          <RoundBar room={room} />

          {status_line && (
            <p className="mt-2 text-center text-[12px] text-ink-dim">{status_line}</p>
          )}

          {!isHost && room.phase === "playing" && (
            <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
              <span className="pill">{room.wordLength} letters</span>
              <span className="pill pill-live">
                {Math.max(0, room.maxGuesses - room.board.length)} guesses left
              </span>
              {room.offListTotal > 0 && (
                <span className={cn("pill", room.offListLeft === 0 && "pill-warn")}>
                  {room.offListLeft} made-up left
                </span>
              )}
            </div>
          )}

          <div className="flex min-h-0 flex-1 items-center justify-center py-3">
            {room.phase === "gameOver" && !showRoundEnd ? (
              <Podium room={room} />
            ) : isHost ? (
              <HostBoardStandIn room={room} />
            ) : room.phase === "lobby" ? (
              <Waiting />
            ) : (
              <Board
                rows={room.board}
                draft={draft}
                wordLength={room.wordLength}
                maxGuesses={room.maxGuesses}
                revealIndex={revealIndex}
                shakeSeq={shakeSeq + localShake}
                solved={!!me?.solved}
              />
            )}
          </div>

          <div className="flex shrink-0 flex-col gap-2.5 pb-1">
            <ReactionBar onReact={(emoji) => send({ type: "react", emoji })} />
            {!isHost && (
              <Keyboard
                letters={room.letters}
                disabled={!canType}
                onKey={type}
                onEnter={submit}
                onBack={back}
              />
            )}
          </div>
        </section>

        {/* ------------------------------------------------------ sidebar */}
        <aside className="flex shrink-0 flex-col gap-3 border-t border-line p-3 lg:w-[300px] lg:overflow-y-auto lg:border-l lg:border-t-0">
          {isHost && (
            <HostPanel
              room={room}
              onSettings={settings}
              onStart={() => send({ type: "round:start" })}
              onEndRound={() => send({ type: "round:end" })}
              onHint={(kind) => send({ type: "hint", kind })}
              onEndGame={() => send({ type: "game:end" })}
              onNewGame={() => send({ type: "game:reset" })}
            />
          )}
          <Players room={room} me={identity?.username ?? null} />
        </aside>
      </div>

      <ReactionStream reactions={reactions} />

      <AnimatePresence>
        {showRoundEnd && <RoundEnd room={room} onClose={() => setDismissed(room.round)} />}
      </AnimatePresence>
    </main>
  );
}

/* -------------------------------------------------------------------------- */

function Waiting() {
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <div className="flex gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.span
            key={i}
            className="tile tile-empty"
            style={{ width: 46, height: 46 }}
            animate={{ borderColor: ["#3a3a3c", "#565758", "#3a3a3c"] }}
            transition={{ duration: 1.6, delay: i * 0.12, repeat: Infinity }}
          />
        ))}
      </div>
      <p className="text-[14px] text-ink-dim">The board fills in when the host starts a round.</p>
    </div>
  );
}

/**
 * The host has no board of their own. They get the shape of the round instead,
 * so they can see how hard they made it without seeing anybody's letters.
 */
function HostBoardStandIn({ room }: { room: RoomState }) {
  const solved = room.players.filter((p) => p.solved).length;

  return (
    <div className="flex flex-col items-center gap-5 text-center">
      {room.phase === "playing" ? (
        <>
          <div className="flex gap-1.5">
            {Array.from({ length: room.wordLength }, (_, i) => (
              <span
                key={i}
                className="tile tile-empty"
                style={{ width: 40, height: 40, fontSize: 16 }}
              />
            ))}
          </div>
          <p className="text-[14px] text-ink-dim">
            {room.wordLength} letters, {room.maxGuesses} guesses. {solved} solved so far.
          </p>
        </>
      ) : (
        <p className="text-[14px] text-ink-dim">No round running.</p>
      )}
    </div>
  );
}
